import { Icon } from '@/components/atoms';
import { useSalesList, useCollaboratorsList, useInventoryBatchesList } from '@/hooks/useErpApi';
import { useNavigate } from 'react-router-dom';

interface KPICardProps {
  title: string;
  value: string | number;
  change?: {
    value: string;
    trend: 'up' | 'down' | 'neutral';
  };
  icon: 'IndianRupee' | 'Users' | 'Package' | 'ShoppingCart';
  iconColor: string;
  iconBg: string;
}

function KPICard({ title, value, change, icon, iconColor, iconBg }: KPICardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-neutral-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-neutral-900 mb-2">{value}</p>
          {change && (
            <div className="flex items-center gap-1">
              <Icon
                name={
                  change.trend === 'up'
                    ? 'TrendingUp'
                    : change.trend === 'down'
                      ? 'TrendingDown'
                      : 'Minus'
                }
                size={16}
                className={
                  change.trend === 'up'
                    ? 'text-success-600'
                    : change.trend === 'down'
                      ? 'text-error-600'
                      : 'text-neutral-500'
                }
              />
              <span
                className={`text-sm font-medium ${
                  change.trend === 'up'
                    ? 'text-success-700'
                    : change.trend === 'down'
                      ? 'text-error-700'
                      : 'text-neutral-600'
                }`}
              >
                {change.value}
              </span>
              <span className="text-sm text-neutral-500">vs last month</span>
            </div>
          )}
        </div>
        <div className={`${iconBg} p-3 rounded-lg`}>
          <Icon name={icon} size={24} className={iconColor} />
        </div>
      </div>
    </div>
  );
}

function ActivityItem({ title, description, time, type }: {
  title: string;
  description: string;
  time: string;
  type: 'info' | 'success' | 'warning' | 'error';
}) {
  const iconMap = { info: 'Info', success: 'CircleCheck', warning: 'TriangleAlert', error: 'CircleAlert' } as const;
  const colorMap = { info: 'text-info-600', success: 'text-success-600', warning: 'text-warning-600', error: 'text-error-600' };
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">
        <Icon name={iconMap[type]} size={16} className={colorMap[type]} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-900">{title}</p>
        <p className="text-sm text-neutral-600">{description}</p>
      </div>
      <span className="text-xs text-neutral-500 whitespace-nowrap">{time}</span>
    </div>
  );
}

function timeSince(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + " years ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + " months ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + " days ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + " hours ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + " minutes ago";
  return Math.floor(seconds) + " seconds ago";
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data: salesData, isLoading: salesLoading } = useSalesList({});
  const { data: collaboratorsData, isLoading: collaboratorsLoading } = useCollaboratorsList();
  const { data: inventoryData, isLoading: inventoryLoading } = useInventoryBatchesList({});

  const sales = salesData || [];
  const collaborators = collaboratorsData || [];
  const inventory = inventoryData || [];

  const totalRevenue = sales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
  const activeMembers = collaborators.filter((c) => c.is_active).length;
  const inventoryCount = inventory.length;
  const salesOrdersCount = sales.length;

  const formatRevenue = (amount: number): string => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };

  const isLoading = salesLoading || collaboratorsLoading || inventoryLoading;

  // Monthly Performance Calculations
  const currentMonthName = new Date().toLocaleString('default', { month: 'long' });
  const currentYear = new Date().getFullYear();
  const firstDayOfMonth = new Date(currentYear, new Date().getMonth(), 1);

  const monthlySales = sales.filter(sale => new Date(sale.created_at) >= firstDayOfMonth);
  const monthlyTotal = monthlySales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
  const monthlyProductsSold = monthlySales.reduce((sum, sale) => sum + ((sale as any).items || []).reduce((itemSum: number, item: any) => itemSum + item.quantity, 0), 0);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">Dashboard</h1>
        <p className="text-neutral-600 mt-2">
          Welcome back! Here&apos;s what&apos;s happening with your FPO today.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total Revenue"
          value={isLoading ? 'Loading...' : formatRevenue(totalRevenue)}
          change={{ value: '+12.5%', trend: 'up' }}
          icon="IndianRupee"
          iconColor="text-success-600"
          iconBg="bg-success-50"
        />
        <KPICard
          title="Active Members"
          value={isLoading ? 'Loading...' : activeMembers}
          change={{ value: '+8', trend: 'up' }}
          icon="Users"
          iconColor="text-primary-600"
          iconBg="bg-primary-50"
        />
        <KPICard
          title="Inventory Items"
          value={isLoading ? 'Loading...' : inventoryCount}
          change={{ value: '-3.2%', trend: 'down' }}
          icon="Package"
          iconColor="text-warning-600"
          iconBg="bg-warning-50"
        />
        <KPICard
          title="Sales Orders"
          value={isLoading ? 'Loading...' : salesOrdersCount}
          change={{ value: '+24%', trend: 'up' }}
          icon="ShoppingCart"
          iconColor="text-info-600"
          iconBg="bg-info-50"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-neutral-900">Recent Activity</h2>
            <button
              onClick={() => navigate('/sales')}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              View All
            </button>
          </div>
          <div className="space-y-4 divide-y divide-neutral-100">
            {isLoading ? (
              <p>Loading activities...</p>
            ) : sales.length === 0 ? (
              <p className="text-neutral-500">No recent sales activity.</p>
            ) : (
              sales.slice(0, 4).map((sale, index) => (
                <div key={sale.id} className={index > 0 ? 'pt-4' : ''}>
                  <ActivityItem
                    title={`New Sale Order #${sale.id.slice(-6)}`}
                    description={`Order received from ${sale.customer_name || 'a customer'}`}
                    time={timeSince(new Date(sale.created_at))}
                    type="success"
                  />
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-6">Quick Actions</h2>
          <div className="space-y-3">
             <button
              onClick={() => navigate('/product-inventory')}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 hover:border-primary-300 transition-all duration-200 group"
            >
              <div className="bg-primary-50 p-2 rounded-lg group-hover:bg-primary-100 transition-colors">
                <Icon name="Plus" size={20} className="text-primary-600" />
              </div>
              <span className="text-sm font-medium text-neutral-900">Add New Product</span>
            </button>
            <button
              onClick={() => navigate('/sales/create')}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 hover:border-primary-300 transition-all duration-200 group"
            >
              <div className="bg-success-50 p-2 rounded-lg group-hover:bg-success-100 transition-colors">
                <Icon name="ShoppingCart" size={20} className="text-success-600" />
              </div>
              <span className="text-sm font-medium text-neutral-900">Create Sale Order</span>
            </button>
            <button
              onClick={() => navigate('/collaborators')}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 hover:border-primary-300 transition-all duration-200 group"
            >
              <div className="bg-info-50 p-2 rounded-lg group-hover:bg-info-100 transition-colors">
                <Icon name="UserPlus" size={20} className="text-info-600" />
              </div>
              <span className="text-sm font-medium text-neutral-900">Add Member</span>
            </button>
            <button
              onClick={() => alert('Report generation coming soon!')}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 hover:border-primary-300 transition-all duration-200 group"
            >
              <div className="bg-warning-50 p-2 rounded-lg group-hover:bg-warning-100 transition-colors">
                <Icon name="FileText" size={20} className="text-warning-600" />
              </div>
              <span className="text-sm font-medium text-neutral-900">Generate Report</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Monthly Performance</h2>
            <p className="text-primary-100 mt-1">{currentMonthName} {currentYear}</p>
          </div>
          <Icon name="TrendingUp" size={32} className="text-primary-200" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-primary-100 text-sm mb-1">Total Transactions</p>
            <p className="text-3xl font-bold">{isLoading ? '...' : formatRevenue(monthlyTotal)}</p>
            <p className="text-primary-100 text-sm mt-2">+15% from last month</p>
          </div>
          <div>
            <p className="text-primary-100 text-sm mb-1">Products Sold</p>
            <p className="text-3xl font-bold">{isLoading ? '...' : monthlyProductsSold.toLocaleString()}</p>
            <p className="text-primary-100 text-sm mt-2">+8% from last month</p>
          </div>
          <div>
            <p className="text-primary-100 text-sm mb-1">New Members</p>
            <p className="text-3xl font-bold">42</p>
            <p className="text-primary-100 text-sm mt-2">+5% from last month</p>
          </div>
        </div>
      </div>
    </div>
  );
}

